import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Euro, Droplets, Gauge, Clock, Receipt, Route, Scale, Coins, Truck } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend, PieChart, Pie, Cell } from "recharts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KpiCard, PageHeader } from "@/components/KpiCard";
import { DailyCostTable } from "@/components/DailyCostTable";
import { api } from "@/lib/api";
import { fmtNum, fmtEur, monthStartISO, todayISO, STATION_COLORS, kmlTone, diffTone } from "@/lib/format";

const Card = ({ title, children, className = "" }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`}>
    <h3 className="mb-4 text-sm font-semibold text-slate-900">{title}</h3>
    {children}
  </div>
);

const tip = { contentStyle: { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 } };

export default function AdminDashboard() {
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats", { params: { date_from: from || undefined, date_to: to || undefined } }).then((r) => setData(r.data));
  }, [from, to]);

  if (!data) return <p className="text-slate-500">Caricamento...</p>;
  const { overall, per_station: ps, daily, pending_count } = data;
  const color = (i) => STATION_COLORS[i % STATION_COLORS.length];
  const pieData = ps.filter((s) => s.total_liters > 0);

  return (
    <div className="fade-up">
      <PageHeader title="Dashboard" subtitle="Panoramica consumi e spesa di tutte le stazioni (esclusi i record segnalati).">
        <div className="flex items-end gap-2">
          <div className="relative space-y-1"><Label className="text-xs">Dal</Label><button type="button" onClick={() => { setFrom(monthStartISO()); setTo(todayISO()); }} className="absolute right-0 top-0 text-xs font-semibold leading-none text-sky-700 hover:underline" data-testid="dashboard-month-reset">Mese in corso</button><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="dashboard-date-from" className="w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="dashboard-date-to" className="w-40" /></div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Importo totale gasolio" value={fmtEur(overall.total_cost)} sub={`${overall.entries} record`} icon={Euro} testId="kpi-total-spend" />
        <KpiCard label="Litri totali" value={fmtNum(overall.total_liters, 0)} sub={`Prezzo medio ${fmtNum(overall.avg_price, 3)} €/L`} icon={Droplets} accent="#10B981" testId="kpi-total-liters" delay={60} />
        <KpiCard label="Media km/l generale" value={fmtNum(overall.avg_km_per_liter, 2)} sub={`${fmtNum(overall.total_km, 0)} km totali`} icon={Gauge} accent="#F59E0B" testId="kpi-avg-kml-overall" delay={120} />
        <Link to="/admin/controllo" data-testid="kpi-pending-link">
          <KpiCard label="Da approvare" value={pending_count} sub="Vai al controllo dati →" icon={Clock} accent="#E11D48" testId="kpi-pending-count" delay={180} />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Rimborso Amazon" value={fmtEur(overall.total_reimbursement)} sub={`${overall.reimbursed_days} giorni con rimborso`} icon={Receipt} accent="#0EA5E9" testId="kpi-total-reimbursement" delay={240} />
        <KpiCard label="Km rimborsati" value={fmtNum(overall.total_reimbursed_km, 0)} icon={Route} accent="#6366F1" testId="kpi-reimbursed-km" delay={300} />
        <div className="fade-up rounded-xl border border-slate-200 bg-white p-5" style={{ animationDelay: "360ms" }}>
          <div className="flex items-start justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Differenza netta (rimborso − gasolio)</p>
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-slate-600"><Scale className="h-4 w-4" /></span>
          </div>
          <p className={`font-mono-num mt-3 text-2xl font-semibold tracking-tight sm:text-3xl ${diffTone(overall.reimbursed_days ? overall.difference : null)}`} data-testid="kpi-difference">{overall.reimbursed_days ? fmtEur(overall.difference) : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Netto IVA 22% · gasolio netto {fmtEur(overall.reimbursed_cost_net)} sui giorni rimborsati</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Costo medio per km (netto IVA)" value={`${fmtNum(overall.cost_per_km_net, 3)} €`} sub={`IVA incl. ${fmtNum(overall.cost_per_km, 3)} €/km${overall.reimbursement_per_km ? ` · rimborso medio ${fmtNum(overall.reimbursement_per_km, 3)} €/km` : ""}`} icon={Coins} accent="#D97706" testId="kpi-cost-per-km" delay={420} />
        <KpiCard label="Costo medio per mezzo / giorno" value={fmtEur(overall.cost_per_vehicle)} sub="Gasolio IVA incl. ÷ mezzi usciti" icon={Truck} accent="#0F766E" testId="kpi-cost-per-vehicle" delay={480} />
        <KpiCard label="Km medi per mezzo / giorno" value={fmtNum(overall.km_per_vehicle, 1)} sub={`Media ${fmtNum(overall.avg_vehicles_per_day, 1)} mezzi usciti al giorno`} icon={Route} accent="#475569" testId="kpi-km-per-vehicle" delay={540} />
      </div>

      <h2 className="mb-3 mt-8 text-base font-semibold text-slate-900 md:text-lg">Per stazione</h2>
      {ps.length === 0 ? (
        <p className="rounded-xl border border-dashed p-6 text-sm text-slate-500" data-testid="no-stations">Nessuna stazione configurata. <Link className="font-semibold text-blue-600" to="/admin/stazioni">Crea le stazioni</Link></p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ps.map((s, i) => (
            <Link to={`/admin/controllo?stazione=${s.station_id}`} key={s.station_id} className="block rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-slate-400" style={{ borderTop: `3px solid ${color(i)}` }} data-testid={`station-kpi-${s.station_id}`}>
              <p className="truncate text-sm font-semibold">{s.station_name}</p>
              <div className="mt-3 flex items-baseline justify-between">
                <span className="font-mono-num text-2xl font-semibold" data-testid={`station-kml-${s.station_id}`}>{fmtNum(s.avg_km_per_liter, 2)}</span>
                <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${kmlTone(s.avg_km_per_liter)}`}>km/l</span>
              </div>
              <div className={`mt-3 rounded-lg px-3 py-2 ${s.reimbursement_per_km && s.cost_per_km_net > s.reimbursement_per_km ? "bg-rose-50 ring-1 ring-rose-200" : "bg-amber-50"}`} data-testid={`station-ckm-box-${s.station_id}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wider ${s.reimbursement_per_km && s.cost_per_km_net > s.reimbursement_per_km ? "text-rose-700" : "text-amber-700"}`}>Costo medio al km</p>
                <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid={`station-ckm-${s.station_id}`}>{fmtNum(s.cost_per_km_net, 3)} € <span className="text-xs font-normal text-slate-500">netto IVA</span></p>
                <p className="font-mono-num text-xs text-slate-500" data-testid={`station-ckm-gross-${s.station_id}`}>{fmtNum(s.cost_per_km, 3)} € IVA incl.{s.reimbursement_per_km ? ` · rimborso ${fmtNum(s.reimbursement_per_km, 3)} €/km` : ""}</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div className="col-span-2">Importo<p className="font-mono-num text-sm font-medium text-slate-900">{fmtEur(s.total_cost)}</p></div>
                <div>Litri<p className="font-mono-num text-sm font-medium text-slate-900">{fmtNum(s.total_liters, 0)}</p></div>
                <div>Mezzi/giorno<p className="font-mono-num text-sm font-medium text-slate-900" data-testid={`station-vehicles-${s.station_id}`}>{fmtNum(s.avg_vehicles_per_day, 1)}</p></div>
                <div>Rimborso<p className="font-mono-num text-sm font-medium text-slate-900">{fmtEur(s.total_reimbursement)}</p></div>
                <div>Km/mezzo<p className="font-mono-num text-sm font-medium text-slate-900">{fmtNum(s.km_per_vehicle, 1)}</p></div>
                <div>€/mezzo<p className="font-mono-num text-sm font-medium text-slate-900" data-testid={`station-cpv-${s.station_id}`}>{fmtEur(s.cost_per_vehicle)}</p></div>
                <div>Diff. netta<p className={`font-mono-num text-sm font-semibold ${diffTone(s.reimbursed_days ? s.difference : null)}`} data-testid={`station-diff-${s.station_id}`}>{s.reimbursed_days ? fmtEur(s.difference) : "—"}</p></div>
              </div>
              <p className="mt-3 text-xs font-medium text-blue-600">Vedi dati stazione →</p>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Card title="Importo gasolio per stazione (€)" className="lg:col-span-7">
          <div className="h-72" data-testid="chart-cost-by-station">
            <ResponsiveContainer>
              <BarChart data={ps}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="station_name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip {...tip} formatter={(v) => fmtEur(v)} />
                <Bar dataKey="total_cost" name="Spesa" radius={[6, 6, 0, 0]}>
                  {ps.map((s, i) => <Cell key={s.station_id} fill={color(i)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Distribuzione litri" className="lg:col-span-5">
          <div className="h-72" data-testid="chart-liters-distribution">
            {pieData.length === 0 ? <p className="grid h-full place-items-center text-sm text-slate-400">Nessun dato</p> : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="total_liters" nameKey="station_name" innerRadius={60} outerRadius={95} paddingAngle={2}>
                    {pieData.map((s) => <Cell key={s.station_id} fill={color(ps.indexOf(s))} />)}
                  </Pie>
                  <Tooltip {...tip} formatter={(v) => `${fmtNum(v, 0)} L`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
        <Card title="Andamento consumo km/l per stazione" className="lg:col-span-12">
          <div className="h-80" data-testid="chart-kml-trend">
            <ResponsiveContainer>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5).split("-").reverse().join("/")} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip {...tip} formatter={(v) => `${fmtNum(v, 2)} km/l`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {ps.map((s, i) => (
                  <Line key={s.station_id} type="monotone" dataKey={`kml_${s.station_id}`} name={s.station_name} stroke={color(i)} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Costo per km giornaliero per stazione (€/km netto IVA)" className="lg:col-span-12">
          <div className="h-80" data-testid="chart-cost-per-km-trend">
            <ResponsiveContainer>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5).split("-").reverse().join("/")} />
                <YAxis tick={{ fontSize: 12 }} domain={["auto", "auto"]} />
                <Tooltip {...tip} formatter={(v) => `${fmtNum(v, 3)} €/km`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {ps.map((s, i) => (
                  <Line key={s.station_id} type="monotone" dataKey={`ckm_${s.station_id}`} name={s.station_name} stroke={color(i)} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Costo per km giornaliero (tutte le stazioni)" className="lg:col-span-12">
          <DailyCostTable daily={daily} />
        </Card>
      </div>
    </div>
  );
}
